// utils/productsData.js
import { get, getFile } from "api/apiBridges/httpBridge";
import { getDataSource } from "api/ds";

/**
 * Универсальная загрузка всех данных по товарам клиента.
 *
 * Возвращает объект:
 * {
 *   dataWB:     Array,           // плоский список размеров WB
 *   dataOZON:   Array,           // плоский список размеров OZON
 *   dataCUSTOM: Array,           // плоский список размеров custom
 *   customMap:  Object,          // { [custom_barcode]: { wb, ozon, id } }
 *   data:       Array,           // объединённый список:
 *                                //   [{ main, wb, ozon }, ...]
 *   used:       { wb: Set, ozon: Set }, // какие баркоды уже связаны
 * }
 *
 * Параметры:
 *  - user_id
 *  - company_id
 *  - showNotification — функция из useLocalNotifications (необязательна).
 *    Если передана, будет показывать ошибки при отсутствии API-ключей
 *    или ошибках загрузки. Если не передана — просто выбрасывает ошибки
 *    в консоль.
 */
export async function fetchAllProductsData({ 
    
    // @ts-ignore
    user_id, 
    // @ts-ignore
    company_id, 
    // @ts-ignore
    showNotification 
} = {}) {
    const ds = getDataSource("ClientProductsModern");

    // Проверка API-ключа (кроме custom)
    const checkToken = async (market) => {
        if (market === "custom") return true;
        return await get("Clients/checkClientAPIKey", { uid: user_id, market });
    };

    // Получение сырых данных с парсера
    const fetchMarketRaw = async (market) => {
        switch (market) {
            case "wb":
                return await ds.getWBCardAndStocks(user_id);
            case "ozon":
                return await ds.getOZONCardAndStocks(user_id);
            case "custom": {
                const res = await get("custom_product/getOnCli", { cid: user_id });
                return res.sort((a, b) => b.nmId - a.nmId);
            }
            default:
                return [];
        }
    };

    // Разворачиваем одну карточку в плоский список размеров
    const flattenItem = (el, market) => {
        const elsizes = el.data.sizes ?? [];
        const productId = market === "custom" ? el.nmID : el.nmID || el.group_id;

        const { sizes: _omit, ...dataWithoutSizes } = el.data;

        const baseCard = {
            ...el,
            data: dataWithoutSizes,
            id: productId,
        };

        return elsizes.map((size) => {
            const dataForSize = {
                ...dataWithoutSizes,
                sizeTitle: size.title,
                barcode: size.barcode,
            };
            if (size.vendorCode) dataForSize.vendorCode = size.vendorCode;

            const obj = {
                ...baseCard,
                id: `${productId};${size.barcode}`,
                data: dataForSize,
                size: {
                    ...size,
                    stocks: [...(size.stocks || [])].sort((a, b) => b.stocks - a.stocks),
                },
            };

            if (market !== "custom" && !obj.nmID && size.nmID) {
                obj.nmID = size.nmID;
            }

            if (market === "custom") {
                obj.qrcode = {
                    id: `${obj.nmID};${size.barcode}`,
                    nmID: obj.nmID,
                    title: dataForSize.title,
                    size,
                    company: company_id,
                    count: size.countQr,
                    vendorCode: dataForSize.vendorCode,
                    market: 1,
                };
                obj.size.countQr = size.countQr || 0;
            }

            return obj;
        });
    };

    // Обработка одного маркета
    const processMarket = async (market) => {
        try {
            const isToken = await checkToken(market);

            if (!isToken) {
                if (showNotification) {
                    showNotification({
                        id: `checkToken_${market}`,
                        type: "error",
                        message: `У вас не указан API ключ для маркета "${market}".`,
                    });
                }
                return [];
            }

            const raw = await fetchMarketRaw(market);
            const flattened = raw.flatMap((el) => flattenItem(el, market));
            console.log(`result [${market}]`, flattened);
            return flattened;
        } catch (err) {
            console.error(`fetchData error [${market}]:`, err);
            if (showNotification) {
                showNotification({
                    id: `fetchDataError_${market}`,
                    type: "error",
                    message: `Ошибка при загрузке данных для маркета "${market}".`,
                });
            }
            return [];
        }
    };

    // Запрос сохранённых связей штрихкодов
    const processSavedBarcodes = async () => {
        try {
            const res = await get("Barcode_search/getBarcode", { uid: user_id });
            const tmp = {};
            if (Array.isArray(res)) {
                for (const link of res) {
                    if (link.custom_barcode) {
                        tmp[link.custom_barcode] = {
                            wb: link.wb_barcode,
                            ozon: link.ozon_barcode,
                            id: link.id,
                        };
                    }
                }
            }
            return tmp;
        } catch (err) {
            console.error("fetchData error [savedBarcodes]:", err);
            return {};
        }
    };

    // Параллельно грузим все маркеты и связи
    const [wb, ozon, custom, customMap] = await Promise.all([
        processMarket("wb"),
        processMarket("ozon"),
        processMarket("custom"),
        processSavedBarcodes(),
    ]);

    // Собираем объединённый список
    const wbByBarcode = new Map(wb.map((item) => [item.size.barcode, item]));
    const ozonByBarcode = new Map(ozon.map((item) => [item.size.barcode, item]));

    const usedWB = new Set();
    const usedOZON = new Set();
    const data = [];

    for (const item of custom) {
        const link = customMap[item.size.barcode];
        if (link) {
            const wbItem = link.wb ? wbByBarcode.get(link.wb) : undefined;
            const ozonItem = link.ozon ? ozonByBarcode.get(link.ozon) : undefined;
            if (wbItem) usedWB.add(wbItem.size.barcode);
            if (ozonItem) usedOZON.add(ozonItem.size.barcode);
            data.push({ wb: wbItem, ozon: ozonItem, main: item });
        } else {
            data.push({ wb: undefined, ozon: undefined, main: item });
        }
    }

    return {
        dataWB: wb,
        dataOZON: ozon,
        dataCUSTOM: custom,
        customMap,
        data,
        used: { wb: usedWB, ozon: usedOZON },
    };
}